import { Injectable }           from '@angular/core';
import { Profile }              from '@app/core/services/authorization/keycloak-service-wrapper';
import { User }                 from '@app/modules/user/models/user.model';
import { AuthorizationService } from '@core/services/authorization/authorization.service';
import {
	CurrentUserStoreActions,
	CurrentUserStoreSelectors
}                               from '@core/store/feature/current-user';
import { ICurrentUserState }    from '@core/store/feature/current-user/state';
import {
	FeatureScopesEnum,
	SupportAdminScopesEnum
}                               from '@models/scope.enums';
import { Store }                from '@ngrx/store';
import { IManageTenant }        from '@shared/content-sections/manage-tenant/manage-tenant.model';
import {
	ManagedObject,
	ManagedObjectTypeEnum
}                               from '@shared/models/managed-object.model';
import {
	ADDITIONAL_ROLES,
	ARCHIVING_ROLES,
	ArchivingRoleTypes,
	CustomerRoleTypes,
	MANAGEMENT_ROLES,
	ManagementRoleTypes,
	RoleTypes,
	SUPPORT_ROLES,
	SupportRoleTypes,
	TENANT_MANAGING_ADMIN_ROLES,
	TenantsManagingAdminRoleTypes
}                               from '@shared/models/roles.model';
import { Observable }           from 'rxjs';
import { take }                 from 'rxjs/operators';


@Injectable( { providedIn: 'root' } )
export class CurrentUserService {

	private _tenantId: string;

	isAuthenticated: boolean;

	isAuthenticated$: Observable<boolean>;

	profile: Profile;

	profile$: Observable<Profile>;

	roles: RoleTypes[];

	roles$: Observable<string[]>;

	currentUserId$: Observable<string>;
	currentUserId: string;

	currentUser$: Observable<User>;
	currentUser: User;

	managedTenant$: Observable<IManageTenant>;

	managedTenant: IManageTenant;

	managedObject: ManagedObject;

	constructor(
		private readonly authorizationService: AuthorizationService,
		private readonly store$: Store
	) {

		this.store$.select( CurrentUserStoreSelectors.getState )
		.subscribe( ( currentUserState: ICurrentUserState ) => {
			this.isAuthenticated = currentUserState.isAuthenticated;
			this.profile = currentUserState.profile;
			this.roles = currentUserState.roles as RoleTypes[];
			this.currentUserId = currentUserState.currentUserId;
			this.currentUser = currentUserState.user;
			this.managedTenant = currentUserState.manageTenant;
			this._tenantId = currentUserState.tenantId;
			this.managedObject = currentUserState.managedObject;
		} );

		this.isAuthenticated$ = this.store$.select( CurrentUserStoreSelectors.isAuthenticated );
		this.profile$ = this.store$.select( CurrentUserStoreSelectors.profile );
		this.roles$ = this.store$.select( CurrentUserStoreSelectors.roles );
		this.currentUserId$ = this.store$.select( CurrentUserStoreSelectors.currentUserId );
		this.currentUser$ = this.store$.select( CurrentUserStoreSelectors.currentUser );
		this.managedTenant$ = this.store$.select( CurrentUserStoreSelectors.manageTenant );
	}

	get tenantId(): string {
		if ( !this._tenantId ) {
			return this.getTenantId();
		}

		return this._tenantId;
	}

	private getCurrentUsersSpecificRoleType( specificRoleTypes: RoleTypes[] ): RoleTypes[] {
		let specificRole = null;

		this.roles$
		.pipe( take( 1 ) )
		.subscribe( ( currentUsersRoles: string[] ) => {
			specificRole = specificRoleTypes.find( ( roleType: RoleTypes ) =>
				currentUsersRoles.includes( roleType )
			);
		} );

		return specificRole;
	}

	getIsAuthenticated(): boolean { return this.isAuthenticated; }

	getIsAuthenticated$(): Observable<boolean> { return this.isAuthenticated$; }

	getManagedObject(): ManagedObject {
		return this.managedObject;
	}

	getProfile(): Profile { return this.profile; }

	getProfile$(): Observable<Profile> { return this.profile$; }

	getRoles(): RoleTypes[] { return this.roles; }

	getRoles$(): Observable<string[]> { return this.roles$; }

	getTenantId( getManagedTenantIdIfExists?: boolean ): string {
		if ( getManagedTenantIdIfExists && this.managedTenant ) {
			return this.managedTenant?.tenantId;
		} else {
			return this._tenantId;
		}
	}

	canAssignRole( role: RoleTypes ): boolean {
		switch ( role ) {
			case RoleTypes.SUPPORT_ADMIN:
				return this.getRoles().includes( RoleTypes.SUPPORT_ADMIN );

			case RoleTypes.CUSTOMER_ADMIN:
				return this.getRoles().includes( RoleTypes.SUPPORT_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.CUSTOMER_ADMIN );

			case RoleTypes.RESELLER_ADMIN:
				return this.getRoles().includes( RoleTypes.SUPPORT_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.RESELLER_ADMIN );

			case RoleTypes.PARTNER_ADMIN:
				return this.getRoles().includes( RoleTypes.SUPPORT_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.RESELLER_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.PARTNER_ADMIN );

			case RoleTypes.DISTRIBUTOR_ADMIN:
				return this.getRoles().includes( RoleTypes.SUPPORT_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.DISTRIBUTOR_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.RESELLER_ADMIN );

			case RoleTypes.VENDOR_ADMIN:
				return this.getRoles().includes( RoleTypes.SUPPORT_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.RESELLER_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.DISTRIBUTOR_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.PARTNER_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.VENDOR_ADMIN );

			case RoleTypes.ACCOUNT_ADMIN:
				return this.getRoles().includes( RoleTypes.SUPPORT_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.RESELLER_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.PARTNER_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.ACCOUNT_ADMIN );

			case RoleTypes.TENANT_ADMIN:
			case RoleTypes.ENTITY_ADMIN:
			case RoleTypes.SITE_ADMIN:
				return this.getRoles().includes( RoleTypes.SUPPORT_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.RESELLER_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.DISTRIBUTOR_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.PARTNER_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.VENDOR_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.ACCOUNT_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.TENANT_ADMIN );

			case RoleTypes.ARCHIVE_ADMIN:
				return this.getRoles().includes( RoleTypes.SUPPORT_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.ARCHIVE_ADMIN );

			case RoleTypes.ARCHIVING_USER:
				return this.getRoles().includes( RoleTypes.SUPPORT_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.ARCHIVE_ADMIN ) ||
							 this.getRoles().includes( RoleTypes.ARCHIVING_USER );

			case RoleTypes.TAXPAYER:
				return this.authorizationService.hasPermission( FeatureScopesEnum.ERIC )
							 ? this.getRoles().includes( RoleTypes.SUPPORT_ADMIN ) ||
								 this.getRoles().includes( RoleTypes.TAXPAYER )
							 : false;

			default:
				return false;
		}
	}

	getCurrentUsersSupportRole(): SupportRoleTypes {
		return this.getCurrentUsersSpecificRoleType( SUPPORT_ROLES ) as unknown as SupportRoleTypes;
	}

	getCurrentUsersTenantAdminRole(): TenantsManagingAdminRoleTypes {
		return this.getCurrentUsersSpecificRoleType( TENANT_MANAGING_ADMIN_ROLES ) as unknown as TenantsManagingAdminRoleTypes;
	}

	getCurrentUsersManagementRole(): ManagementRoleTypes {
		return this.getCurrentUsersSpecificRoleType( MANAGEMENT_ROLES ) as unknown as ManagementRoleTypes;
	}

	getCurrentUsersArchivingRole(): ArchivingRoleTypes {
		return this.getCurrentUsersSpecificRoleType( ARCHIVING_ROLES ) as unknown as ArchivingRoleTypes;
	}

	getCurrentUsersCustomerRole(): CustomerRoleTypes {
		return this.getCurrentUsersSpecificRoleType( ADDITIONAL_ROLES ) as unknown as CustomerRoleTypes;
	}

	hasCurrentUserSupportRole(): boolean {
		return this.authorizationService.hasPermission( SupportAdminScopesEnum.IS_SUPPORT_ADMIN );
	}

	hasCurrentUserTenantAdminRole(): boolean {
		return !!this.getCurrentUsersTenantAdminRole();
	}

	hasCurrentUserManagementRole(): boolean {
		return !!this.getCurrentUsersManagementRole();
	}

	hasCurrentUserArchivingRole(): boolean {
		return !!this.getCurrentUsersArchivingRole();
	}

	hasCurrentUserCustomerRole(): boolean {
		return !!this.getCurrentUsersCustomerRole();
	}

	getCurrentUserId$(): Observable<string> { return this.currentUserId$; }

	getCurrentUserId(): string { return this.currentUserId; }

	getCurrentUser$(): Observable<User> { return this.currentUser$; }

	getCurrentUser(): User { return this.currentUser; }

	storeCurrentUser( user: User ): void {
		this.store$.dispatch( new CurrentUserStoreActions.SetCurrentUserAction( user ) );
	}
}
